// Data type for the base64 data string and mime type
interface Base64Data {
    base64DataUrl: string;
    mimeType: string;
}

// A map to cache the base64 data url for items in PST files
// this way we can support downloading PST items
const itemBase64DataMap = new Map<string, Base64Data>();

// Adds base64 data for an item to the map
// The base64 data is formatted as a data URL with the mime type
export function addItemDataBase64Blob(
    itemId: string,
    base64String: string,
    mimeType: string
): void {
    try {
        const blob = new Blob([Uint8Array.from(atob(base64String), c => c.charCodeAt(0))], {
            type: mimeType,
        });
        const base64DataUrl = URL.createObjectURL(blob);

        // Revoke any existing ObjectURL for this item to prevent memory leaks
        const existing = itemBase64DataMap.get(itemId);
        if (existing) {
            URL.revokeObjectURL(existing.base64DataUrl);
        }

        itemBase64DataMap.set(itemId, { base64DataUrl, mimeType });
    } catch {
        // atob() can throw on malformed base64 data — skip caching on failure
    }
}

// Removes base64 data for an item from the map
// This is used when items go out of view in the ListView
// so we don't keep the data in memory for too long
export function removeItemDataBase64Blob(itemId: string): void {
    const base64Data = itemBase64DataMap.get(itemId);
    if (base64Data) {
        URL.revokeObjectURL(base64Data.base64DataUrl);
        itemBase64DataMap.delete(itemId);
    }
}

// Clears all base64 data from the map
// This is used when the ListView changes tableViewId
export function clearItemDataBase64Blob(): void {
    for (const base64Data of itemBase64DataMap.values()) {
        URL.revokeObjectURL(base64Data.base64DataUrl);
    }
    itemBase64DataMap.clear();
}

// Gets the base64 data for an item from the map
// This is used when the user drags the item to download it
export function getItemDataBase64Blob(itemId: string): Base64Data | undefined {
    return itemBase64DataMap.get(itemId);
}

// Checks if the base64 data for an item exists in the map
// This is used so we don't try fetch the Blob again if we already have it
// and before downloading the item in PST scenarios
export function hasItemDataBase64Blob(itemId: string): boolean {
    return itemBase64DataMap.has(itemId);
}
